import { FunctionComponent } from "react";
import { Media, SentimentPricing } from "core/types/social.types";
import { Button, Grid, Paper, useTheme } from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import { MessageCircle } from "react-feather";
import { Text } from "components/atoms/text";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { abbreviateNumber } from "core/helpers/abbreviate-number";

const useStyles = makeStyles(() => ({
  root: {
    height: "100%",
    minHeight: "390px",
    "& > *": {
      boxShadow:
        "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19) !important",
    },
  },
}));

interface Props {
  post: Media;
  selectedPosts?: Media[];
  handleClickPost?: (post: Media) => void;
  sentiment?: SentimentPricing;
  pricingMode?: boolean;
}

const Post: FunctionComponent<Props> = ({
  post,
  selectedPosts,
  sentiment,
  handleClickPost,
  pricingMode = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isPostSelected = selectedPosts
    ? selectedPosts.includes(post)
    : undefined;

  return (
    <div className={classes.root}>
      <Paper
        elevation={3}
        sx={{
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "20px",
          width: "300px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderWidth: "1px",
          borderColor: isPostSelected
            ? theme.palette.primary.main
            : "transparent",
          borderStyle: "solid",
        }}
      >
        <Grid container>
          <img
            src={post.mediaUrl}
            alt={post.mediaUrl}
            width={"100%"}
            height={"268px"}
            style={{
              objectFit: "cover",
            }}
          />
          <Grid item container spacing={2}>
            <Grid item display={"flex"} alignItems={"center"}>
              <FavoriteRoundedIcon color="error" />
              <Text sx={{ marginLeft: "5px" }}>
                {abbreviateNumber(post.likeCount)}
              </Text>
            </Grid>
            <Grid item display={"flex"} alignItems={"center"}>
              <MessageCircle />
              <Text sx={{ marginLeft: "5px" }}>
                {abbreviateNumber(post.commentsCount)}
              </Text>
            </Grid>
          </Grid>
          <Grid item p={"10px 0px"}>
            <Text
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
              }}
            >
              {post.caption}
            </Text>
          </Grid>
        </Grid>
        <Grid container spacing={4} pb={"10px"}>
          {sentiment && (
            <>
              <Grid item xs={12} sm={5}>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {t("social-dashboard.post.tkns")}
                </Text>
                <Text fontSize={"12px"}>{sentiment?.CoDeTokens}</Text>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {t("social-dashboard.post.analyzed")}
                </Text>
                <Text fontSize={"12px"}>{sentiment?.totalCommentsAnalyzed}</Text>
              </Grid>
            </>
          )}
        </Grid>
        {isPostSelected !== undefined && (
          <Grid container>
            <Button
              variant={isPostSelected ? "contained" : "outlined"}
              fullWidth
              onClick={() => handleClickPost && handleClickPost(post)}
              disabled={pricingMode ? !sentiment : false}
            >
              {isPostSelected
                ? t("social-dashboard.deselect")
                : t("social-dashboard.select")}
            </Button>
          </Grid>
        )}
      </Paper>
    </div>
  );
};

export default Post;
